import Moment from "moment";

type SupportedInterval = "hour" | "day" | "week" | "month" | "quarter" | "year";

const SUPPORTED_INTERVALS: SupportedInterval[] = [
  "hour",
  "day",
  "week",
  "month",
  "quarter",
  "year",
];

const normalizeInterval = (interval?: string): SupportedInterval | null => {
  if (!interval) return null;
  const i = interval.toLowerCase() as SupportedInterval;
  return SUPPORTED_INTERVALS.includes(i) ? i : null;
};

const parseMomentSafe = (v: any): Moment.Moment | null => {
  if (v === null || v === undefined) return null;
  const m = Moment(v);
  return m.isValid() ? m : null;
};

const uniqByString = (arr: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of arr) {
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
};

const buildFirstByRawString = (parsed: Array<{ raw: any }>): Map<string, any> => {
  const firstByRawString = new Map<string, any>();
  for (const p of parsed) {
    const key = String(p.raw);
    if (!firstByRawString.has(key)) firstByRawString.set(key, p.raw);
  }
  return firstByRawString;
};

const countIntervalsInclusive = (
  start: Moment.Moment,
  end: Moment.Moment,
  interval: SupportedInterval
): number => {
  if (!start.isValid() || !end.isValid()) return 0;
  if (end.isBefore(start)) return 0;

  // Moment doesn't have "quarter" as a diff unit in all builds; treat as 3 months.
  if (interval === "quarter") {
    const months = end.diff(start, "months");
    return Math.floor(months / 3) + 1;
  }

  const unit = interval as any;
  return end.diff(start, unit) + 1;
};

export interface GetTimeSeriesTicksOptions {
  timeField: string;
  maxTicks: number;
  interval?: string;
}

/**
 * Fallback for when the x-axis values are not parseable as dates (e.g. cyclical
 * buckets like "Monday"/"Tuesday"). Treat them as ordered categorical labels in the
 * data's own (plotting) order and evenly sample down to maxTicks so the axis still
 * shows labels instead of vanishing.
 */
const getCategoricalTicks = (data: any[], timeField: string, maxTicks: number): any[] => {
  const firstByKey = new Map<string, any>();
  for (const d of data) {
    const v = d?.[timeField];
    if (v === null || v === undefined) continue;
    const key = String(v);
    if (!firstByKey.has(key)) firstByKey.set(key, v);
  }

  const uniqueKeys = Array.from(firstByKey.keys());
  if (uniqueKeys.length === 0) return [];
  if (maxTicks === 1) return [firstByKey.get(uniqueKeys[0])];
  if (uniqueKeys.length <= maxTicks) return uniqueKeys.map((k) => firstByKey.get(k));

  const step = (uniqueKeys.length - 1) / (maxTicks - 1);
  const idxs = Array.from({ length: maxTicks }, (_, i) => Math.round(i * step));
  const selectedKeys = uniqByString(
    idxs.map((i) => uniqueKeys[Math.max(0, Math.min(uniqueKeys.length - 1, i))])
  );
  return selectedKeys.map((k) => firstByKey.get(k));
};

/**
 * Pick <= maxTicks x-axis tick values spanning the full time domain.
 * Keeps the underlying data untouched; this is only for axis labels.
 */
export const getTimeSeriesTicks = (data: any[], options: GetTimeSeriesTicksOptions): any[] => {
  const { timeField, maxTicks, interval } = options;

  if (!Array.isArray(data) || data.length === 0) return [];
  if (!Number.isFinite(maxTicks) || maxTicks <= 0) return [];

  // Collect and sort unique time values.
  const parsed = data
    .map((d) => {
      const raw = d?.[timeField];
      const m = parseMomentSafe(raw);
      return m ? { raw, m } : null;
    })
    .filter((x): x is { raw: any; m: Moment.Moment } => x !== null)
    .sort((a, b) => a.m.valueOf() - b.m.valueOf());

  // No date-parseable values (e.g. cyclical weekday buckets like "Monday"): fall back
  // to categorical labels so the x-axis still renders instead of coming up empty.
  if (parsed.length === 0) return getCategoricalTicks(data, timeField, maxTicks);

  // Used throughout to map de-duped string keys back to original raw values.
  const firstByRawString = buildFirstByRawString(parsed);

  // If already within budget, return the unique raw values in chronological order.
  const rawValuesSorted = uniqByString(parsed.map((p) => String(p.raw)));
  if (maxTicks === 1) {
    const onlyKey = rawValuesSorted[0];
    return onlyKey === undefined ? [] : [firstByRawString.get(onlyKey)];
  }
  if (rawValuesSorted.length <= maxTicks) {
    return rawValuesSorted.map((k) => firstByRawString.get(k));
  }

  const start = parsed[0].m.clone();
  const end = parsed[parsed.length - 1].m.clone();

  const normalizedInterval = normalizeInterval(interval);

  // Prefer interval-aware strides when an interval is provided.
  if (normalizedInterval) {
    const numIntervals = countIntervalsInclusive(start, end, normalizedInterval);
    const stride = Math.max(1, Math.ceil(numIntervals / maxTicks));

    const ticks: any[] = [];
    for (const p of parsed) {
      // Map each datum to its interval index from start, then pick stride intervals.
      let intervalIndex: number;
      if (normalizedInterval === "quarter") {
        intervalIndex = Math.floor(p.m.diff(start, "months") / 3);
      } else {
        intervalIndex = p.m.diff(start, normalizedInterval as any);
      }

      if (intervalIndex % stride === 0) {
        ticks.push(p.raw);
      }
    }

    // Ensure start/end are included and dedupe while keeping order.
    const ticksWithBounds = [parsed[0].raw, ...ticks, parsed[parsed.length - 1].raw];
    const dedupedKeys = uniqByString(ticksWithBounds.map((t) => String(t)));
    const deduped = dedupedKeys.map((k) => firstByRawString.get(k));

    // If still over budget due to duplicates/resolution, fall back to index-based sampling.
    if (deduped.length <= maxTicks) return deduped;
  }

  // Fallback: evenly sample indices over the sorted unique time values.
  const uniqueRaw = rawValuesSorted;
  const step = (uniqueRaw.length - 1) / (maxTicks - 1);
  const idxs = Array.from({ length: maxTicks }, (_, i) => Math.round(i * step));
  const selectedKeys = uniqByString(
    idxs.map((i) => uniqueRaw[Math.max(0, Math.min(uniqueRaw.length - 1, i))])
  );

  return selectedKeys.map((k) => firstByRawString.get(k));
};

